// A lean forecast's detail (contract, events, history, …), read by id (`GET /api/forecasts/by-id`)
// only when it is needed: when the forecast comes on screen, or when it has a newer forecast to
// promote. There is no whole-list download: a forecast nobody opens stays lean, and its card reads
// the lean summary (lib/forecast/listSource.js).
//
// Lean (`detailPending`) runs are read by their server id (`forecastId`), the viewer's own and
// those shared with them alike. A read that keeps failing marks its runs (detailBackfill.js), so
// the open forecast's lane stops loading and draws what the lean row has.
import {
  selectAllRuns,
  selectOpenRootIds,
  selectRun,
  selectRunTree,
} from "../lib/forecast/selectors.js";
import type { RunState } from "../lib/forecast/reducer.js";
import { sharedLiveVintages } from "../lib/forecast/sharedLiveVintages.js";
import { markDetailFailed, clearDetailFailed } from "../lib/forecast/detailBackfill.js";
import {
  selectNewestReforecast,
  shouldApplyNewestReforecast,
} from "../lib/forecast/reforecastEntries.js";
import { recordUiEvent } from "../lib/logRingBuffer";
import { reportFailure } from "../lib/failureCounts.js";
import { BY_ID_MAX_IDS } from "../services/forecastApi.js";
import {
  applyNewestReforecastAll,
  deliveredVintage,
  detailPatches,
  fillLeanDetail,
  noteDeliveredVintages,
  resetDeliveredVintages,
} from "./loadForecasts.js";
import { detailRefreshed } from "../lib/forecast/reducer.js";
import { STATUS } from "../lib/forecast/model.js";
import { isSettled, isVoided } from "../lib/forecast/postResolutionRuns.js";
import { execTable } from "../store/execTable.js";
import { mergeReportPointers } from "../lib/forecast/reportPointerMerge.js";
import type { CommandDeps } from "./deps.js";
import type { Run } from "../lib/forecast/model.js";

/**
 * Bounded, backed-off retries for one batch: an immediate retry hurts a struggling server, and an
 * endless one would loop from every open tab.
 */
const RETRY_DELAYS_MS = [1_000, 4_000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** How long after a run began its own on-demand report pointer can be stamped (hasNewerForecast). */
const OWN_POINTER_WINDOW_MS = 2 * 60 * 1000;

/** One by-id request; a rejection (the service never throws, but a fake may) reads as `!ok`. */
async function readBatch(
  deps: CommandDeps,
  forecastIds: string[],
): Promise<{ ok: boolean; forecasts: any[]; error?: unknown }> {
  try {
    return await deps.services.api.listForecastsById(forecastIds);
  } catch (error) {
    return { ok: false, forecasts: [], error };
  }
}

/**
 * The lean runs that make up the forecasts rooted at `rootIds`: each root and its branches, found
 * through their parents. A shared branch can arrive on a later list page than its parent, which
 * leaves it a page-local `conversationId`, so the tree is walked from the topmost parent present.
 */
export function leanRunIdsOf(deps: CommandDeps, rootIds: string[]): string[] {
  const state = deps.store.getState();
  const out = new Set<string>();
  for (const id of rootIds) {
    for (const run of selectRunTree(state, topOf(state, id))) {
      if (run.detailPending) out.add(run.runId);
    }
  }
  return [...out];
}

/** The topmost run above `runId` that the store holds, following `parentRunId`. */
export function topOf(state: RunState, runId: string): string {
  const seen = new Set<string>();
  let id = runId;
  for (;;) {
    const parent = selectRun(state, id)?.parentRunId;
    if (!parent || seen.has(parent) || !selectRun(state, parent)) return id;
    seen.add(id);
    id = parent;
  }
}

/**
 * Whether a lean run has a newer forecast than the one it stores: a scheduled or panel fire in
 * its report pointers, later than its live moment (the lean summary's last history snapshot, or
 * its creation). Promoting one needs its detail, so such a run is read even off screen.
 */
export function hasNewerForecast(run: Run): boolean {
  if (!run.detailPending) return false;
  // Runs are replaced, never mutated, on every change, so an unchanged run's answer is reused:
  // the installer asks this of every lean run on every store change.
  const cached = newerByRun.get(run);
  if (cached !== undefined) return cached;
  const newer = computeHasNewerForecast(run);
  newerByRun.set(run, newer);
  return newer;
}

const newerByRun = new WeakMap<Run, boolean>();

function computeHasNewerForecast(run: Run): boolean {
  const history = (run as any).listSummary?.history;
  const last = Array.isArray(history) && history.length ? history[history.length - 1] : null;
  const liveMomentIso = (last && last.snapshotAt) || run.createdAt || null;
  const pointers = run.reportPointers ?? [];
  const newest = selectNewestReforecast(liveMomentIso, pointers);
  if (newest.kind !== "async" || !shouldApplyNewestReforecast(run, newest.fireTs)) return false;
  // The run's own on-demand report: the forecast server stamps it from its own clock a moment
  // after the run began, so it reads as newer. The pickup recognises it only by fetching it
  // (reforecastPickup sameAnswer), which is not worth a read off screen; a different on-demand
  // answer in that window is still promoted when the forecast is opened.
  const pointer = (pointers as any[]).find((p) => p?.row_key === newest.rowKey);
  const sinceLiveMs = Date.parse(newest.fireTs) - Date.parse(liveMomentIso ?? "");
  if (pointer?.source === "ondemand" && sinceLiveMs <= OWN_POINTER_WINDOW_MS) return false;
  return true;
}

/**
 * Read the detail of `runIds` (lean runs, owned or shared) and fill the ones still lean when it
 * lands, promoting each one's newest forecast. A run is read by its server id (`forecastId`); one
 * never saved has none and nothing to read. A batch is retried twice; then its runs are marked
 * failed. Never throws. Resolves to the number of runs filled.
 */
export async function loadForecastDetail(
  deps: CommandDeps,
  runIds: string[],
  cancelled: () => boolean = () => false,
): Promise<number> {
  const state = deps.store.getState();
  const runIdOf = new Map<string, string>();
  for (const id of runIds) {
    const fid = selectRun(state, id)?.forecastId;
    if (fid) runIdOf.set(fid, id);
  }
  const unreadable = runIds.filter((id) => !selectRun(state, id)?.forecastId);
  if (unreadable.length) markDetailFailed(unreadable);
  const forecastIds = [...runIdOf.keys()];
  clearDetailFailed(forecastIds.map((fid) => runIdOf.get(fid)!));
  let filled = 0;
  let failedBatches = 0;
  let lastError: unknown;
  for (let i = 0; i < forecastIds.length; i += BY_ID_MAX_IDS) {
    const batch = forecastIds.slice(i, i + BY_ID_MAX_IDS);
    if (cancelled()) return filled;
    let r = await readBatch(deps, batch);
    for (let k = 0; !r.ok && k < RETRY_DELAYS_MS.length; k++) {
      if (cancelled()) return filled;
      await sleep(RETRY_DELAYS_MS[k]);
      if (cancelled()) return filled;
      r = await readBatch(deps, batch);
    }
    if (cancelled()) return filled;
    if (!r.ok) {
      failedBatches += 1;
      if (r.error !== undefined) lastError = r.error;
      markDetailFailed(batch.map((fid) => runIdOf.get(fid)!));
      continue;
    }
    const named = r.forecasts.filter((row: any) => runIdOf.has(row.forecastId ?? ""));
    filled += fillLeanDetail(deps, named, cancelled).length;
    // A named run the answer left out (deleted, or no longer shared, meanwhile) has nothing
    // more to wait for.
    const answered = new Set(named.map((row: any) => row.forecastId));
    const missing = batch.filter((fid) => !answered.has(fid)).map((fid) => runIdOf.get(fid)!);
    if (missing.length) markDetailFailed(missing);
  }
  recordUiEvent({
    event: "forecasts.detailLoadedById",
    requested: forecastIds.length,
    n: filled,
    failedBatches,
  });
  if (failedBatches) {
    reportFailure(
      "forecasts.detail_by_id_failed",
      lastError ?? new Error(`${failedBatches} by-id batches answered !ok after retries`),
    );
  }
  return filled;
}

/** The moment of a history's last saved vintage, in ms (NaN when it has none). */
function lastSnapshotMs(history: unknown): number {
  const last = Array.isArray(history) && history.length ? history[history.length - 1] : null;
  return Date.parse(last?.snapshotAt ?? "");
}

/**
 * Whether a list row's history (`listed`) ends on a later saved vintage than the latest one `run`
 * holds or was delivered: the run's own history can stop short of what the server sent once a
 * resolution has stepped it back (loadForecasts.ts deliveredVintage).
 */
function listsNewerVintage(listed: unknown, run: Run): boolean {
  const t = lastSnapshotMs(listed);
  const known = [lastSnapshotMs(run.history), deliveredVintage(run.runId)].filter(Number.isFinite);
  return Number.isFinite(t) && (!known.length || t > Math.max(...known));
}

/** Whether history `a` ends on a later saved vintage than `b`; any vintage is later than none. */
function endsLater(a: unknown, b: unknown): boolean {
  const ta = lastSnapshotMs(a);
  const tb = lastSnapshotMs(b);
  return Number.isFinite(ta) && (!Number.isFinite(tb) || ta > tb);
}

/**
 * Runs whose refresh is owed: marked when a reload finds them changed, cleared once a read is
 * answered. A failed read keeps the mark, so the next reload retries even when what changed (a
 * finished status) is already in the store and no longer looks like a change.
 */
const refreshOwed = new Set<string>();

/**
 * Each run's latest refresh read: only the answer to the newest one may land, so two overlapping
 * reads cannot apply out of order (an earlier "running" answer over a later completion).
 */
const refreshGeneration = new Map<string, number>();
let refreshSeq = 0;

/** Test-only reset. */
export function resetRefreshOwed(): void {
  refreshOwed.clear();
  refreshGeneration.clear();
  resetDeliveredVintages();
}

const inFlight = (status: string | undefined) =>
  status === STATUS.RUNNING || status === STATUS.PREPROCESSING;

/**
 * Whether a lean row (`incoming`) shows that a run whose detail is loaded (`prior`) has since
 * changed on the server: a later saved vintage than its history (the owner saved an update), or a
 * run that was in flight and is no longer (it finished, perhaps without a new history entry, while
 * this tab missed the event). Read before the row is applied. A run this tab is executing
 * (execTable, compare legs included) is never "changed": its own stream lands its result.
 */
export function savedVintageChanged(
  prior: Run | undefined,
  incoming: Run | undefined,
  { resolutionLands = false }: { resolutionLands?: boolean } = {},
): boolean {
  if (!prior || !incoming || prior.detailPending) return false;
  // A run this tab is executing lands its own result; a read now would only race it.
  if (execTable.has(prior.runId)) return false;
  // A resolved run holds its history stepped back past later vintages; voided, it keeps them, so
  // the history it was delivered (and trimmed) must be read again. Only where the row's void
  // lands on the run (the shared-list reload), or the read would be trimmed again.
  if (resolutionLands && isSettled(prior) && isVoided(incoming)) return true;
  if (refreshOwed.has(prior.runId)) return true;
  if (listsNewerVintage((incoming as any).listSummary?.history, prior)) return true;
  return inFlight(prior.status) && !inFlight(incoming.status);
}

/**
 * Re-read the detail of `runIds` (runs whose detail is loaded but older than what the server now
 * holds, savedVintageChanged) and replace it with the answer, with the answer's finished status
 * when the run still reads as in flight (a finish this tab missed); a status this tab settled is
 * never replaced. The detail shown stays until then; an answer older than the
 * run's history, one for a run this tab is executing, or one a newer refresh of the same run has
 * superseded, is dropped; a failed read leaves the detail, and is reported.
 * Never throws.
 */
export async function refreshDetail(
  deps: CommandDeps,
  runIds: string[],
  cancelled: () => boolean = () => false,
): Promise<void> {
  const state = deps.store.getState();
  const runIdOf = new Map<string, string>();
  const generation = ++refreshSeq;
  for (const id of runIds) {
    refreshOwed.add(id);
    refreshGeneration.set(id, generation);
    const fid = selectRun(state, id)?.forecastId;
    if (fid) runIdOf.set(fid, id);
    else refreshOwed.delete(id);
  }
  const forecastIds = [...runIdOf.keys()];
  for (let i = 0; i < forecastIds.length; i += BY_ID_MAX_IDS) {
    if (cancelled()) return;
    const batch = forecastIds.slice(i, i + BY_ID_MAX_IDS);
    const r = await readBatch(deps, batch);
    if (cancelled()) return;
    if (!r.ok) {
      reportFailure("forecasts.detail_by_id_failed", r.error ?? new Error("detail refresh !ok"));
      continue;
    }
    // Answered: whatever it holds, this read settled the refresh (a run left out of the answer
    // is gone or no longer shared).
    // A newer read of the same run owns its settlement.
    for (const fid of batch) {
      const id = runIdOf.get(fid) as string;
      if (refreshGeneration.get(id) === generation) refreshOwed.delete(id);
    }
    const now = deps.store.getState();
    const fresh = r.forecasts.filter((row: any) => {
      const runId = runIdOf.get(row.forecastId ?? "");
      const run = runId ? selectRun(now, runId) : null;
      // A run this tab is executing lands its own result. Any other run takes the newer detail
      // whatever its stored status says (a completion this tab missed leaves it "running"); the
      // status itself is never taken from here.
      return (
        !!run &&
        refreshGeneration.get(runId as string) === generation &&
        !execTable.has(runId as string) &&
        !endsLater(run.history, row.history)
      );
    });
    if (!fresh.length) continue;
    // A finish this tab missed lands with the detail: only over a run still in flight, never
    // over a status this tab settled itself (an errored rerun stays errored).
    const patches = detailPatches(fresh);
    for (const row of fresh) {
      const runId = runIdOf.get(row.forecastId) as string;
      const run = selectRun(now, runId);
      if (inFlight(run?.status) && typeof row.status === "string" && !inFlight(row.status)) {
        patches[runId].status = row.status;
      }
    }
    deps.store.dispatch(detailRefreshed(patches) as any);
    noteDeliveredVintages(fresh);
    const after = deps.store.getState();
    // Promote from the fresh rows only: their history is the live moment a fire must beat.
    const promotable = fresh.filter((row: any) => {
      const runId = runIdOf.get(row.forecastId);
      return after.runsById[runId as string] || sharedLiveVintages();
    });
    applyNewestReforecastAll(
      promotable.map((row: any) => ({
        ...row,
        reportPointers: mergeReportPointers(
          selectRun(after, runIdOf.get(row.forecastId) as string)?.reportPointers,
          row.reportPointers,
        ),
      })),
      cancelled,
    );
  }
}

/**
 * Read each lean forecast's detail when it is needed: when it comes on screen (selected or
 * compared, its root and branches), or when it has a newer forecast to promote (at boot, or when
 * a realtime update adds one). Each run is asked for once while its read is pending or done; a
 * run whose read failed is asked again when it next comes on screen. Returns the uninstall.
 */
export function installOpenForecastDetail(deps: CommandDeps): () => void {
  // Runs whose read is pending or done, and runs whose read left them lean (retried only when
  // what is on screen changes, never on every store change).
  const asked = new Set<string>();
  const retryOnOpen = new Set<string>();
  let uninstalled = false;
  let lastRoots = new Set<string>();
  const read = (runIds: string[]) => {
    for (const id of runIds) {
      asked.add(id);
      retryOnOpen.delete(id);
    }
    void loadForecastDetail(deps, runIds, () => uninstalled)
      .then(() => {
        const after = deps.store.getState();
        for (const id of runIds) {
          if (!selectRun(after, id)?.detailPending) continue;
          asked.delete(id);
          retryOnOpen.add(id);
        }
      })
      .catch((err: unknown) => reportFailure("forecasts.detail_by_id_failed", err));
  };
  const check = () => {
    const state = deps.store.getState();
    const rootIds = selectOpenRootIds(state);
    // A failed run is retried only when its forecast comes (back) on screen, never because
    // another forecast joined or left the comparison, nor on every store change.
    const newlyOpen = new Set(
      leanRunIdsOf(
        deps,
        rootIds.filter((id) => !lastRoots.has(id)),
      ),
    );
    lastRoots = new Set(rootIds);
    const due = (id: string) => !asked.has(id) && (!retryOnOpen.has(id) || newlyOpen.has(id));
    const onScreen = leanRunIdsOf(deps, rootIds).filter(due);
    // A shared run is promoted only while shared live vintages are on, so only then is it read
    // for a newer forecast.
    const candidates = sharedLiveVintages() ? selectAllRuns(state) : Object.values(state.runsById);
    const newer = candidates
      .filter((run) => !asked.has(run.runId) && !retryOnOpen.has(run.runId))
      .filter(hasNewerForecast)
      .map((run) => run.runId);
    const runIds = [...new Set([...onScreen, ...newer])];
    if (runIds.length) read(runIds);
  };
  const unsubscribe = deps.store.subscribe(check);
  check();
  return () => {
    uninstalled = true;
    unsubscribe();
  };
}
